import { useRef, useState } from "react";
import { Mail, MapPin, Phone } from "lucide-react";
import { ReactComponent as SearchIcon } from "@icons/search.svg";
import logo from "@images/logo.svg";

const menuLinks = [
  "О сервисе",
  "Подписка",
  "Премьеры",
  "Сертификаты",
  "Партнерам",
  "Новости",
  "Контакты",
];

const catalogColumns = [
  [
    "Фантастика",
    "Боевики",
    "Драмы",
    "Фэнтези",
    "Ужасы",
    "Триллеры",
    "Комедии",
  ],
  [
    "Аниме",
    "Документальные",
    "Детективы",
    "Биографии",
    "Мелодрамы",
    "Криминал",
    "Приключения",
  ],
];

const officeContacts = [
  {
    icon: Phone,
    label: "Поддержка:",
    value: "8 800 555 35 35",
  },
  {
    icon: MapPin,
    label: "Компания:",
    value: "SMOTRI Streaming Service",
  },
  {
    icon: Mail,
    label: "Почта:",
    value: "support@smotri.tv",
  },
];

const supportContacts = [
  {
    icon: Phone,
    label: "Для партнеров:",
    value: "+7 (495) 120 20 20",
  },
  {
    icon: MapPin,
    label: "Юр. информация:",
    value: "Пользовательское соглашение и политика конфиденциальности",
  },
];

function FooterLink({ children }) {
  return (
    <button
      className="inter-r flex h-5 items-center border-0 bg-transparent p-0 text-left text-sm leading-[140%] text-[#D9D9D9] transition-colors hover:text-white"
      type="button"
    >
      {children}
    </button>
  );
}

function LinkList({ items, className = "" }) {
  return (
    <div className={`flex flex-col items-start gap-[10px] ${className}`}>
      {items.map((item) => (
        <FooterLink key={item}>{item}</FooterLink>
      ))}
    </div>
  );
}

function ColumnTitle({ children }) {
  return (
    <div className="jost-m flex h-6 items-center text-base leading-6 text-white">
      {children}
    </div>
  );
}

function FooterSearchInput({ placeholder }) {
  const [searchValue, setSearchValue] = useState("");
  const searchInputRef = useRef(null);

  return (
    <div className="relative flex h-[46px] w-[280px] items-center overflow-hidden rounded-xl bg-white/[0.18] backdrop-blur-[10px]">
      <button
        aria-label="Search"
        className="absolute left-3 top-1/2 z-[2] flex h-5 w-5 -translate-y-1/2 items-center justify-center border-0 bg-transparent p-0"
        onClick={() => searchInputRef.current?.focus()}
        type="button"
      >
        <SearchIcon width={20} height={20} />
      </button>
      <input
        className="h-full w-full rounded-xl border-0 bg-transparent px-[46px] py-0 text-sm text-white opacity-100 outline-none placeholder:text-white/56"
        onChange={(e) => setSearchValue(e.target.value)}
        placeholder={placeholder}
        ref={searchInputRef}
        type="text"
        value={searchValue}
      />
      <button
        aria-label="Clear search"
        className={`absolute right-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full border-0 bg-white/[0.18] p-0 text-white transition-opacity ${
          searchValue ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={(event) => {
          event.stopPropagation();
          setSearchValue("");
          searchInputRef.current?.focus();
        }}
        onMouseDown={(event) => event.preventDefault()}
        type="button"
      >
        &times;
      </button>
    </div>
  );
}

function ContactItem({ icon: Icon, label, value }) {
  return (
    <div className="flex flex-col items-start gap-[3px]">
      <div className="flex h-6 items-start gap-[5px]">
        <Icon className="h-6 w-6 text-[#717280]" strokeWidth={1.5} />
        <div className="inter-r flex h-5 items-center text-sm leading-[140%] text-white">
          {label}
        </div>
      </div>
      <div className="inter-m pl-[30px] text-base leading-6 text-[#979797]">
        {value}
      </div>
    </div>
  );
}

function ContactColumn({ title, contacts, withSearch = false }) {
  return (
    <div className="flex h-[296px] w-full flex-col items-start gap-6">
      <ColumnTitle>{title}</ColumnTitle>
      <div className="flex flex-col items-start gap-[18px]">
        {contacts.map((contact) => (
          <ContactItem {...contact} key={contact.label} />
        ))}
      </div>
      {withSearch && (
        <div className="mt-auto">
          <FooterSearchInput placeholder="Поиск..." />
        </div>
      )}
    </div>
  );
}

export default function Footer() {
  return (
    <footer className="w-full bg-black pt-[60px] text-white">
      <div className="border-t border-[#2A2A2A] px-4 pb-8 pt-[60px] sm:px-8 xl:px-20">
        <div className="mx-auto flex w-full max-w-[1760px] flex-col items-start">
          <img
            className="h-[41px] w-[170px] object-contain object-left"
            src={logo}
            alt="SMOTRI"
          />

          <div className="mt-[18px] h-px w-full bg-[#1F1F1F]" />

          <div className="grid w-full grid-cols-[198px_277px_304px_333px] items-start justify-between gap-6 py-8">
            <LinkList className="w-[198px]" items={menuLinks} />

            <div className="flex w-[277px] flex-col items-start gap-3">
              <ColumnTitle>Каталог</ColumnTitle>
              <div className="flex items-start gap-6">
                {catalogColumns.map((column, index) => (
                  <LinkList
                    className={index === 0 ? "w-[111px]" : "w-[142px]"}
                    items={column}
                    key={index}
                  />
                ))}
              </div>
            </div>

            <ContactColumn title="Помощь" contacts={officeContacts} />

            <ContactColumn
              title="Компания"
              contacts={supportContacts}
              withSearch
            />
          </div>

          <div className="h-px w-full bg-[#2A2A2A]" />

          <div className="flex h-[55.19px] w-full items-start pt-[18px]">
            <button
              className="inter-r flex h-5 items-center border-0 bg-transparent p-0 text-base leading-[120%] text-[#ADB5BD]"
              type="button"
            >
              © 2026 SMOTRI Co. Ltd.
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
